import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { isCampaignActive } from "../utils/campaignAvailability";
import "./SeasonalCampaign.css";

const CAMPAIGNS_URL = "/app/campaigns.json";
const PRODUCTS_URL = "/app/products.json";

function formatPrice(value, currency = "EUR") {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency,
  }).format(Number(value) || 0);
}

function getRegularPrice(product, productMap) {
  const compareAtPrice = Number(product.compareAtPrice);

  if (Number.isFinite(compareAtPrice) && compareAtPrice > Number(product.price)) {
    return compareAtPrice;
  }

  if (!Array.isArray(product.bundleItems) || !product.bundleItems.length) {
    return 0;
  }

  let total = 0;

  for (const item of product.bundleItems) {
    const bundledProduct = productMap.get(item.productId);
    const quantity = Number(item.quantity) || 0;
    const price = Number(bundledProduct?.price);

    if (!bundledProduct || !Number.isFinite(price) || quantity <= 0) {
      return 0;
    }

    total += price * quantity;
  }

  return Number(total.toFixed(2));
}

function resolveOffers(campaign, products) {
  const productMap = new Map(
    products
      .filter((product) => product?.active)
      .map((product) => [product.id, product]),
  );

  return (campaign.offers || []).flatMap((offer) => {
    const product = productMap.get(offer.productId);

    if (!product) return [];

    const regularPrice = getRegularPrice(product, productMap);
    const campaignPrice = Number(product.price) || 0;
    const savings = Math.max(0, regularPrice - campaignPrice);
    const savingsPercentage = regularPrice
      ? Math.round((savings / regularPrice) * 100)
      : 0;

    return [
      {
        ...offer,
        product,
        regularPrice,
        savings,
        savingsPercentage,
      },
    ];
  });
}

export default function SeasonalCampaign({ placement = "home" }) {
  const [activeCampaign, setActiveCampaign] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    async function loadCampaign() {
      try {
        const requestOptions = {
          cache: "no-store",
          signal: controller.signal,
        };
        const [campaignsResponse, productsResponse] = await Promise.all([
          fetch(CAMPAIGNS_URL, requestOptions),
          fetch(PRODUCTS_URL, requestOptions),
        ]);

        if (!campaignsResponse.ok || !productsResponse.ok) {
          throw new Error("No se pudieron cargar los datos de campaña");
        }

        const [campaignsData, productsData] = await Promise.all([
          campaignsResponse.json(),
          productsResponse.json(),
        ]);
        const now = Date.now();
        const campaign = (campaignsData.campaigns || [])
          .filter((item) =>
            isCampaignActive(item, { placement, now }),
          )
          .sort((a, b) => (Number(b.priority) || 0) - (Number(a.priority) || 0))[0];

        if (!campaign) {
          setActiveCampaign(null);
          return;
        }

        const offers = resolveOffers(campaign, productsData.products || []);

        setActiveCampaign(offers.length ? { ...campaign, offers } : null);
      } catch (error) {
        if (error.name !== "AbortError") {
          setActiveCampaign(null);
        }
      }
    }

    loadCampaign();

    return () => controller.abort();
  }, [placement]);

  if (!activeCampaign) return null;

  return (
    <section
      className="seasonal-campaign"
      data-theme={activeCampaign.theme || "default"}
      aria-labelledby={`campaign-${activeCampaign.id}-title`}
    >
      <header className="seasonal-campaign__header">
        <p>{activeCampaign.eyebrow}</p>
        <h2 id={`campaign-${activeCampaign.id}-title`}>
          {activeCampaign.title}
        </h2>
        <span>{activeCampaign.description}</span>
      </header>

      <div className="seasonal-campaign__offers">
        {activeCampaign.offers.map((offer) => {
          const { product } = offer;

          return (
            <article
              key={product.id}
              className={`seasonal-campaign-card${
                offer.featured ? " seasonal-campaign-card--featured" : ""
              }`}
            >
              <div className="seasonal-campaign-card__media">
                <img
                  src={product.image}
                  alt={product.imageAlt || product.name}
                  loading="lazy"
                  decoding="async"
                />
                {offer.savingsPercentage > 0 && (
                  <strong>{offer.savingsPercentage} % de ahorro</strong>
                )}
              </div>

              <div className="seasonal-campaign-card__content">
                {offer.featured && <small>Oferta destacada</small>}
                <h3>{product.name}</h3>
                <p>{product.shortDescription}</p>

                <div className="seasonal-campaign-card__price">
                  {offer.regularPrice > 0 && (
                    <span>
                      Antes <del>{formatPrice(offer.regularPrice, product.currency)}</del>
                    </span>
                  )}
                  <strong>{formatPrice(product.price, product.currency)}</strong>
                  {offer.savings > 0 && (
                    <em>
                      Ahorras {formatPrice(offer.savings, product.currency)}
                    </em>
                  )}
                </div>

                <Link
                  className="seasonal-campaign-card__cta"
                  to={`/oferta/${product.id}`}
                >
                  {offer.ctaLabel}
                  <ArrowRight aria-hidden="true" />
                </Link>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
